import { isRouteErrorResponse, useRouteError } from "react-router-dom";
import { RefreshCw, Home } from "lucide-react";
import { Button } from "@/components/ui/button";

const CONTACT_EMAIL = "dev@transparentimaging.com";

/** Human-readable detail for the collapsible "technical details" block. Kept out
 *  of the main copy so non-technical users aren't shown a stack trace. */
function errorDetail(error: unknown): string {
  if (isRouteErrorResponse(error)) {
    return `${error.status} ${error.statusText}`;
  }
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === "string") {
    return error;
  }
  try {
    return JSON.stringify(error);
  } catch {
    return "Unknown error";
  }
}

/**
 * Route-level error boundary. React Router renders this in place of a route
 * whenever a descendant throws during render, a loader, or an action — instead
 * of the raw dev error overlay. Friendly, reassuring copy with a couple of
 * recovery actions; the technical detail is tucked into a collapsed section.
 */
/** Build a mailto: link pre-filled with a subject and the technical context so
 *  users can report the problem without having to describe or copy anything. */
function buildMailtoHref(detail: string): string {
  const subject = "Scheduler error report";
  // Report origin + pathname only — the query string and hash can contain
  // Auth0 callback tokens (code/state/access_token) that must not land in email.
  const page = `${window.location.origin}${window.location.pathname}`;
  const body = [
    "Hi team,",
    "",
    "I hit an error in the scheduler. Details below (auto-filled):",
    "",
    `Page: ${page}`,
    `Time: ${new Date().toISOString()}`,
    `Browser: ${navigator.userAgent}`,
    "",
    "Error:",
    detail,
    "",
    "What I was doing when it happened:",
    "",
  ].join("\n");
  return `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

export default function ErrorPage() {
  const error = useRouteError();
  const detail = errorDetail(error);
  const mailtoHref = buildMailtoHref(detail);

  return (
    <div className="relative flex min-h-dvh w-full flex-col items-center justify-center overflow-hidden bg-background px-6">
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.03] dark:opacity-[0.06]"
        style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)`,
          backgroundSize: "32px 32px",
        }}
      />

      <div className="relative z-10 flex w-full max-w-md flex-col items-center gap-8 rounded-2xl border bg-card px-10 py-12 shadow-xl">
        <div className="flex h-16 w-16 items-center justify-center rounded-full border-2 border-muted bg-muted/40">
          <svg
            className="h-7 w-7 text-muted-foreground"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
            <path d="M12 9v4" />
            <path d="M12 17h.01" />
          </svg>
        </div>

        <div className="flex flex-col items-center gap-3 text-center">
          <h1 className="text-xl font-semibold tracking-tight text-foreground">
            Something went wrong
          </h1>
          <p className="text-sm leading-relaxed text-muted-foreground">
            Sorry — this page hit an unexpected problem. Try
            reloading, and if it keeps happening let us know.
          </p>
        </div>

        <div className="flex w-full flex-col gap-3">
          <Button className="w-full" onClick={() => window.location.reload()}>
            <RefreshCw className="h-4 w-4" />
            Reload page
          </Button>
          {/* Plain anchor (not client-side nav) so a full reload clears any bad state. */}
          <Button asChild variant="outline" className="w-full">
            <a href="/">
              <Home className="h-4 w-4" />
              Back to schedule
            </a>
          </Button>
        </div>

        <div className="flex w-full flex-col items-center gap-3">
          <p className="text-xs text-muted-foreground">
            Still stuck? Email{" "}
            <a
              href={mailtoHref}
              className="font-medium text-foreground underline-offset-4 hover:underline"
            >
              {CONTACT_EMAIL}
            </a>
          </p>
          <details className="w-full">
            <summary className="cursor-pointer text-center text-xs text-muted-foreground/70 hover:text-muted-foreground">
              Technical details
            </summary>
            <pre className="mt-2 max-h-32 overflow-auto rounded-md border bg-muted/40 p-2 text-[10px] leading-snug text-muted-foreground whitespace-pre-wrap break-words">
              {detail}
            </pre>
          </details>
        </div>
      </div>
    </div>
  );
}
